import React from "react";
import { AppType, LocalStorageKey, MimeTypeEnum } from "./enums";
import {
  containsHtmlTags,
  htmlColorRegex,
  htmlTagsRegex,
  nameKeyRegex,
  nameInitialsRegex,
  fileNameRegex
} from "./regex";
import { getFromStorage } from "./storage";
import { AnyObject } from "../../global";
import { FormFieldOption } from "shared/form/fields/types";
import {
  ALL_FILTER_KEY,
  DIGITS_AFTER_DECIMAL,
  DEFAULT_LOCALE,
  PERCENTAGE_SYMBOL,
  DEFAULT_NAME_INITIALS_CHAR_COUNT,
  BYPASS_MYEXP_WRAPPERS_FOR_ROUTES
} from "./constants";
import startsWith from "lodash/startsWith";
import Tokens from "./storage/tokens";
import upperFirst from "lodash/upperFirst";
import { ThemeList } from "shared/components/themeSelector/themeList";
import isString from "lodash/isString";
import get from "lodash/get";
import isObject from "lodash/isObject";
import sortBy from "lodash/sortBy";
import capitalize from "lodash/capitalize";
import LocalStorage from "./storage/LocalStorage";
import CurrencyCode from "shared/enums/CurrencyCode";
import { ApiAmount } from "shared/types/ApiAmount";
import { getEnvironmentInfo } from "./config";
import QueryParamKey from "shared/enums/QueryParamKey";
import { Copies } from "../hooks/useCopies";
import ApiInventoryTaxConfigurationBasic from "../types/ApiInventoryTaxConfigurationBasic";
import i18n from "../types/I18n";
import { BooleanOption } from "shared/enums/BooleanOption";
import {
  BuyerProgramsList,
  MeetupPrograms,
  ProgramType,
  UnscheduledMeetingsPurchaseAllowedProgramsList
} from "shared/enums/ProgramType";
import { ProgramParticipantType } from "shared/enums/ProgramParticipantType";
import BuyersProgramStatus from "shared/enums/BuyersProgramStatus";
import MeetupProgramStatus from "shared/enums/MeetupProgramStatus";
import InPersonMeetupProgramStatus from "shared/enums/InPersonMeetupProgramStatus";
import TabletalkProgramStatus from "shared/enums/TabletalkProgramStatus";
import AssociatedProgramStatus from "shared/enums/AssociateProgramStatus";
import TradeProgramStatus from "shared/enums/TradeProgramStatus";
import { StateStatus } from "shared/enums/StateStatus";
import { GetFullNameArgsType, filterFunctionConfig } from "./type";
import { formatValue } from "react-currency-input-field";
import { StaticDataObject } from "../../context/staticData/staticData";
import { DimensionType } from "shared/form/core/assetUpload/types";
import isBoolean from "lodash/isBoolean";
import ConnectionsProgramStatus from "../enums/ConnectionsProgramStatus";
import isNaN from "lodash/isNaN";
import TransactionMethodType from "shared/enums/TransactionMethodType";
import { RedirectQueryParam } from "shared/enums/Redirect";
import { MyExpRoute } from "apps/myExperience/routes/types";
import ApiTimeSessionResponse from "shared/types/ApiTimeSessionResponse";
import { MIME_TYPE_FILE_EXT_MAP } from "../constants";
import LocaleIdMap from "../enums/LocaleIdMap";
import replace from "lodash/replace";
import ToolTip from "shared/components/tooltip";
import Size from "shared/enums/Size";
import TransactionIdentifierDisplayType from "shared/enums/TransactionIdentifierDisplayType";
import Icon from "assets/icons";
import isRegExp from "lodash/isRegExp";
import { AxiosResponse } from "axios";
import { UseMutateAsyncFunction } from "react-query";
import { ApiClientInput } from "shared/datasource/type";
import ApiRegistrationEnabledFeatures from "shared/types/ApiRegistrationEnabledFeatures";
import PdfTemplateName from "shared/enums/PdfTemplateName";
import { Base64Util } from "./base64";

/**
 * Get the token of logged in Participant or User
 * @returns token if available else null
 */
export function getToken(): string {
  return getFromStorage(LocalStorageKey.TOKEN);
}

export function getTransientToken(): string {
  return Tokens.getTransitToken();
}

export function applyTrimAndRemoveEmpty(values: any) {
  return removeEmptyFields(getTrimmedValues(values));
}

export function removeEmptyFields(obj: any) {
  return JSON.parse(JSON.stringify(obj, replaceEmptyString));
}

export function replaceEmptyString(key: string, value: string) {
  return value === "" ? undefined : value;
}

export function getTrimmedValues(o: any) {
  return JSON.parse(
    JSON.stringify(o)
      .replace(/\\"/g, "__pt__")
      .replace(/"\s+|\s+"/g, '"')
      .replace(/__pt__/g, '\\"')
  );
}

/**
 *
 * @param str - string containing placeholders to be replaced
 * @param obj - object containing placeholder key and its value
 * @returns - resolved string
 */
export function getResolvedString(str: string, obj: AnyObject): string {
  const keys = Object.keys(obj);
  return keys.reduce((convStr, key) => convStr.replace(`{${key}}`, obj[key]), str);
}

/**
 *
 * @param str - string containing placeholders to be replaced
 * @param obj - object containing placeholder key and its value
 * @returns - resolved path
 */
export function getResolvedPath(str: string, obj: AnyObject): string {
  const keys = Object.keys(obj);
  return keys.reduce((convStr, key) => convStr.replaceAll(`:${key}`, obj[key]), str);
}

/**
 * @description Get an array of objects that can be passed to render fields which take options as a parameter
 * @param optionsArray array of string or number
 * @returns array of objects and each object will have 2 keys: label and value. Both label and value will be string even if array of number was passed
 */
export function convertArrayToOptions(optionsArray: Array<string | number> | null): Array<FormFieldOption> {
  // if empty object or array with no elements then return empty array
  if (!optionsArray?.length) return [];
  return optionsArray.map(option => ({ value: `${option}`, label: `${option}` }));
}

export function isEmpty(value: any): boolean {
  const type = typeof value;
  switch (type) {
    case "boolean":
    case "number":
    case "function":
      return false;
    case "string":
      return value.trim().length === 0;
    case "object":
      if (value === null) {
        return true;
      } else if (value instanceof Array) {
        return value.length === 0;
      } else if (isRegExp(value)) {
        return false;
      } else {
        return Object.keys(value).length === 0;
      }
    case "undefined":
      return true;
    default:
      return false;
  }
}

export const toNameKey = (value: string) => {
  if (value && value.length > 0) {
    return value
      .toLowerCase()
      .replace(nameKeyRegex, "")
      .replace(/ø/g, "o")
      .replace(/[^a-z0-9]/g, "");
  }
  return "";
};

/**
 * @description set the default support email as received in event obj
 * */
let supportEmail: string;
export function setDefaultSupportEmail(email: string) {
  supportEmail = email;
}

/**
 * @description get the support email as per application if available else default email
 * */
export function getDefaultSupportEmail() {
  return supportEmail;
}

/**
 * @description Checks if a url starts with a protocol
 * @param url Url string to check
 * @returns true if url starts with a protocol(secure/unsecure)
 */
function startsWithProtocol(url: string) {
  return startsWith(url, "http://") || startsWith(url, "https://");
}

/**
 * @description For getting correct format(with protocol) of url in links
 * @param val Url string to check
 * @returns Url with protocol, if not present
 */
export function getUrlForHref(val: string) {
  if (startsWithProtocol(val)) {
    return val;
  } else {
    return "http://" + val;
  }
}

/**
 * @description returns selected locale, if locale is not selected the returns DEFAULT_LOCALE
 * @returns en-US, de-DE
 */
export function getLocaleId(): string {
  return LocalStorage.getItem(LocalStorageKey.LOCALE) || DEFAULT_LOCALE;
}

/**
 * Update
 * @param userLocale
 */
export function setLocaleId(userLocale: string) {
  const isAdminConsole = getEnvironmentInfo().appType === AppType.CONSOLE;
  if (isAdminConsole) {
    //Set locale for console.
    const selectedLocaleId = LocalStorage.getItem(LocalStorageKey.LOCALE);
    if (userLocale !== selectedLocaleId) {
      LocalStorage.setItem(LocalStorageKey.LOCALE, userLocale);
      //if user locale is other than default then reloading page to get copies as per locale
      window.location.reload();
    }
  } else {
    // Set locale for MyExp and Reg portals
    LocalStorage.setItem(LocalStorageKey.LOCALE, userLocale);
  }
}

/**
 * @description returns regKey of a registration
 */
export function getRegKey(): string {
  return LocalStorage.getItem(LocalStorageKey.REG_KEY);
}

/**
 * @description update regKey in storage
 * @param regKey
 */
export function setRegKey(regKey: string) {
  const isAdminConsole = getEnvironmentInfo().appType === AppType.CONSOLE;
  if (!isAdminConsole) {
    LocalStorage.setItem(LocalStorageKey.REG_KEY, regKey);
  }
}

/**
 * @description Returns currency symbol and isPrefix(true when symbol is added prefix in formatting) of given currency in specified locale.
 * if locale is not passed then it takes en-US as default locale.
 */
export function getCurrencySymbol(
  currencyCode: CurrencyCode,
  locale: string = DEFAULT_LOCALE,
  addSpace: boolean = true
) {
  const currencyParts = new Intl.NumberFormat(locale, { style: "currency", currency: currencyCode }).formatToParts(
    Number()
  );
  const currencyIndex = currencyParts.findIndex(item => item.type === "currency");
  const isPrefix = currencyIndex === 0;
  let currencySymbol = currencyParts?.[currencyIndex]?.value;
  if (currencySymbol && isPrefix && addSpace) {
    switch (currencyCode) {
      case CurrencyCode.SGD:
        if (locale === LocaleIdMap.SINGAPORE) {
          currencySymbol = `${currencySymbol} `;
        }
        break;
      default:
        currencySymbol = `${currencySymbol} `;
    }
  }
  return { symbol: currencySymbol, isPrefix };
}

export function getFormattedNumberOrCurrency(args: {
  currencyCode?: CurrencyCode;
  localeId?: string;
  value: number | string;
  formatValue?: boolean;
}) {
  const localeId = args.localeId || getLocaleId();
  const currencySymbol = args.currencyCode && getCurrencySymbol(args.currencyCode, localeId);
  return formatValue({
    value: args.value?.toString(),
    disableGroupSeparators: !args.formatValue,
    decimalScale: getMinimumFractionDigits(parseFloat(args.value as string)),
    prefix: currencySymbol?.isPrefix ? currencySymbol.symbol : undefined,
    intlConfig: { locale: localeId, currency: args.currencyCode }
  }).replace(/\u00A0/g, " ");
}

/**
 * @description Returns minimum digit after decimal. For integer 0 and for Decimal value DIGITS_AFTER_DECIMAL
 */
export function getMinimumFractionDigits(value: number) {
  return value % 1 === 0 ? 0 : DIGITS_AFTER_DECIMAL;
}

export function getFormattedCurrency(amountObj: ApiAmount, locale: string = getLocaleId()) {
  if (!amountObj || amountObj.value === null || amountObj.value === undefined || !amountObj.currencyCode) {
    return "";
  }

  return getFormattedNumberOrCurrency({
    currencyCode: amountObj.currencyCode,
    value: amountObj.value,
    localeId: locale,
    formatValue: true
  });
}

export function getFormattedNumber(numberVal: number | string, locale: string = getLocaleId()) {
  if (isEmpty(numberVal) || isNaN(parseFloat(numberVal as string))) {
    return "";
  }
  const parsedNumber = parseFloat(numberVal as string);
  return Intl.NumberFormat(locale, {
    minimumFractionDigits: getMinimumFractionDigits(parsedNumber),
    maximumFractionDigits: DIGITS_AFTER_DECIMAL
  }).format(parsedNumber);
}

export function getPercentageDisplayValue(value: string | number, locale: string = getLocaleId()) {
  const formattedVal = getFormattedNumber(value, locale);
  return `${formattedVal}${PERCENTAGE_SYMBOL}`;
}

export function getFullName(obj: GetFullNameArgsType) {
  if (!obj) return "";
  const { salutation = "", firstName = "", lastName = "" } = obj;
  return upperFirst([salutation, firstName, lastName].filter(Boolean).join(" "));
}

export function getOptionsFromEnumWithKeyAsLabel(StringEnum: any) {
  return Object.keys(StringEnum).map(label => {
    return {
      label,
      value: StringEnum[label as keyof typeof StringEnum]
    };
  });
}

export function convertStringToNumber(numberStr: string, isDecimal = true): number {
  return isDecimal ? parseFloat(numberStr?.trim()) : parseInt(numberStr?.trim());
}

/**
 * Converts KB into Bytes
 * @param KB
 * @returns bytes
 */
export function getKBInBytes(KB: number) {
  return KB * 1024;
}
/**
 * Converts KB into MB
 * @param KB
 * @returns MB
 */
export function getKBInMB(KB: number) {
  return KB / 1024;
}

export function getBytesInMB(bytes: number) {
  return getKBInMB(bytes / 1024);
}

export function getNameInitials(
  name: string,
  {
    bypassRestrictions = false,
    charCount = DEFAULT_NAME_INITIALS_CHAR_COUNT
  }: { bypassRestrictions?: boolean; charCount?: number } = {}
) {
  if (!name) {
    return "";
  }
  if (bypassRestrictions)
    return name
      .trim()
      .split(" ")
      .map(word => word[0]?.toUpperCase())
      .join("")
      .substring(0, charCount);

  return name
    .replace(nameInitialsRegex, "")
    .replace(/\d+/g, "")
    .trim()
    .split(" ")
    .map(word => word[0]?.toUpperCase())
    .join("")
    .substring(0, charCount);
}

/**
 * @description Function that takes theme name and overides respective theme variables from theme json
 * @param themeName string that represents theme name
 */

export function updateTheme(themeName: string) {
  // remove existing style tags
  const existingStyles = document.getElementById("themeOverrides");
  existingStyles?.remove();

  if (themeName) {
    // extract target theme
    const themeVariables: any = getThemeVariables(themeName);

    if (themeVariables) {
      // create style tag and set id
      const styleTag = document.createElement("style");
      styleTag.setAttribute("id", "themeOverrides");

      // contruct theme literal based on variables to override and add to style tag
      const innerString = Object.keys(themeVariables).reduce((acc, property) => {
        return acc + `${property}: ${themeVariables[property]};`;
      }, "");
      styleTag.innerHTML = `.appType--reg{${innerString}}`;
      document.body.appendChild(styleTag);
    }
  }
}

export function getThemeVariables(themeName: string) {
  const themeObject = ThemeList.find(t => t.name === themeName);
  return themeObject?.variables;
}

export function getTheme(themeName: string) {
  const themeObject = ThemeList.find(t => t.name === themeName);
  return themeObject;
}

/** 
 * Use this to determine parent route path dynamically.
   Takes current location pathname, removes last string after / and returns rest. 
*/
export function getParentRoutePath(pathname: string) {
  const pathArray = pathname?.split("/");
  return pathArray.slice(0, pathArray.length - 1).join("/");
}

/**
 * @description Use this common function on listing screen for filters
 */
export function applySearchFilter<T>({
  data,
  searchParams,
  filter = filterFunction
}: {
  data: Array<T>;
  searchParams: URLSearchParams;
  filter?: (config: filterFunctionConfig) => Array<T>;
}) {
  const parentFilterValue = searchParams.get(QueryParamKey.PARENT_FILTER) || undefined;
  const parentFilterKey = searchParams.get(QueryParamKey.PARENT_KEY) || undefined;

  const value = searchParams.get(QueryParamKey.FILTER) || undefined;
  const key = searchParams.get(QueryParamKey.KEY) || undefined;

  const filteredByParentFilter = filter({
    data,
    key: parentFilterKey,
    value: parentFilterValue
  });

  return filter({
    data: filteredByParentFilter,
    key,
    value
  });
}

/**
 * @description Use this function get filtered list by providing key and value,
 */
export function filterFunction<T>({ data, key, value }: filterFunctionConfig): Array<T> {
  if (key && value && value.toLowerCase?.() !== ALL_FILTER_KEY.toLowerCase()) {
    return data.filter((d: any) => {
      if (key.includes(".")) {
        // Handling for . key
        const keyArray = key.split(".");
        const dataItem = get(d, keyArray[0]);
        if (Array.isArray(dataItem)) {
          return !!dataItem.find(item => get(item, keyArray[1]) === value);
        }
        if (isObject(dataItem)) {
          return get(dataItem, keyArray[1]) === value;
        }
      } else {
        const dataItem = get(d, key);
        if (isString(dataItem)) {
          return dataItem === value;
        }

        if (Array.isArray(dataItem)) {
          return dataItem.includes(value);
        }

        if (isBoolean(dataItem) && dataItem) {
          return true;
        }
      }

      return false;
    });
  }
  return data;
}
/**
 * @param arr - array of objects containing displayOrder key in object
 * @param sourceIndex - index from where the object was dragged
 * @param destinationIndex - index where the object was dropped
 * @returns array with updated displayOrder
 */
export function getUpdatedArrayAfterDragAndDrop({
  arr,
  sourceIndex,
  destinationIndex
}: {
  arr: Array<AnyObject>;
  sourceIndex: number;
  destinationIndex: number;
}) {
  return sortBy(arr, "displayOrder").map((obj, idx) => ({
    ...obj,
    displayOrder: getDisplayOrderForDragAndDrop({
      currentIndex: idx,
      sourceIndex,
      destinationIndex
    })
  }));
}

/**
 * @description - Function that will return the updated display order when using drag and drop.
 *              - Assumes that the array on which we are calling map is already sorted.
 * @param currentIndex - index of current object
 * @param sourceIndex - index from where the object was dragged
 * @param destinationIndex - index where the object was dropped
 * @returns updated display order number for the object
 */
export function getDisplayOrderForDragAndDrop({
  currentIndex,
  sourceIndex,
  destinationIndex
}: {
  currentIndex: number;
  sourceIndex: number;
  destinationIndex: number;
}) {
  let newDisplayOrder = currentIndex;
  if (currentIndex === sourceIndex) {
    newDisplayOrder = destinationIndex;
  } else if (destinationIndex > sourceIndex && currentIndex > sourceIndex && currentIndex <= destinationIndex) {
    // handling for middle elements when the item was dragged down in list
    newDisplayOrder -= 1;
  } else if (destinationIndex < sourceIndex && currentIndex >= destinationIndex && currentIndex < sourceIndex) {
    // handling for middle elements when the item was dragged up in list
    newDisplayOrder += 1;
  }
  return newDisplayOrder + 1;
}

export function getEmptyStringIfValueNullOrUndefined(val?: number | string | null | boolean): string {
  if (val === null || val === undefined) {
    return "";
  }
  return "" + val;
}

/**
 * @description - Function that will return the updated string value after trimming the HTML   tags
 * @param str - string
 * @returns updated string
 */

export function removeHtmlTagsFromString(str: string) {
  if ((str: string) => containsHtmlTags.test(str)) {
    str = str.toString();
    return str.replace(htmlTagsRegex, "");
  } else {
    return str;
  }
}

export function convertBase64StringToUrl(base64Data: string, contentType: string = "application/unknown") {
  return `data:${contentType};base64,${base64Data}`;
}

/**
 * @description download data into file
 * @param data - expects raw data returned by api or if JSON then send stringified JSON
 * @param fileName - name and format of file for download
 */
export function downloadFileUsingData(data: any, fileName: string) {
  const blob = new Blob([data]);
  const url = URL.createObjectURL(blob);
  downloadFileFromUrl(url, fileName);
}

export function downloadFileUsingBase64Data(props: {
  base64Data: string;
  fileName: string;
  contentType?: MimeTypeEnum;
}) {
  const fileNameWithExt = `${props.fileName}${getFileExtFromContentType(props.contentType)}`;
  const base64Url = convertBase64StringToUrl(props.base64Data, props.contentType);
  downloadFileFromUrl(base64Url, fileNameWithExt);
}

/**Use this to download response type blob from api */
export async function downloadBlobFile(args: {
  downloadFn: UseMutateAsyncFunction<any, any, ApiClientInput, unknown>;
  params: AnyObject;
  prefixForFileName?: string;
}) {
  try {
    const response: AxiosResponse = await args.downloadFn({
      params: args.params,
      responseType: "blob",
      parseDataInResponse: false
    });
    downloadBlobFileFromResponse({ response, prefixForFileName: args.prefixForFileName });
    return true;
  } catch (err) {}
}

export function downloadBlobFileFromResponse(args: { response: AxiosResponse; prefixForFileName?: string }) {
  const filename = (args.prefixForFileName ?? "") + args.response.headers["content-disposition"].split("filename=")[1];
  const url = URL.createObjectURL(args.response.data);
  downloadFileFromUrl(url, filename);
}

export function downloadFileFromUrl(url: string, fileName: string) {
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", sanitizeFileName(fileName));
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
}

/**
 *  Other than Alphanumeric and - all other characters will be replace with underscore _
 */
export function sanitizeFileName(fileName: string) {
  return fileName.trim().replace(fileNameRegex, "_");
}

export function isValidHtmlColor(value?: string) {
  if (!value?.trim()) return false;
  return htmlColorRegex.test(value?.trim() as string);
}

export function getValidRgbHexOrEmptyString(value?: string) {
  if (!value?.trim() || !isValidHtmlColor(value)) return "";
  if (startsWith(value.trim(), "#")) {
    return value.trim();
  }
  return `#${value.trim()}`;
}

export function getEntityTaxDisplayLabel(
  copies: Copies,
  copyKey: keyof i18n,
  taxDetails?: ApiInventoryTaxConfigurationBasic
) {
  if (!taxDetails || !taxDetails.taxLabel) {
    return "";
  }
  return copies.get(copyKey, {
    0: getPercentageDisplayValue(taxDetails.taxPercentage),
    1: taxDetails.taxLabel,
    2: taxDetails.inclusive ? copies.get("tax.inclusive") : copies.get("tax.exclusive")
  });
}

export function getBooleanOptions(copies: Copies) {
  return [
    {
      label: copies.get("yes"),
      value: BooleanOption.YES
    },
    {
      label: copies.get("no"),
      value: BooleanOption.NO
    }
  ];
}

export function getYesNoFieldInitialValue(value?: boolean) {
  if (isEmpty(value)) return "";
  return value ? BooleanOption.YES : BooleanOption.NO;
}

export function isTabletalkProgram(programType?: ProgramType) {
  return programType === ProgramType.TABLETALK_PROGRAM;
}

export function isTabletalkParticipant(programParticipantType?: ProgramParticipantType) {
  return programParticipantType === ProgramParticipantType.TABLETALK_PARTICIPANT;
}

export function isFormDirty(formRef: React.MutableRefObject<any>) {
  if (typeof formRef.current?.dirty === "function") {
    return formRef.current?.dirty?.();
  }
  return formRef.current?.dirty;
}

export function isMeetupProgram(programType?: ProgramType) {
  return MeetupPrograms.includes(programType as ProgramType);
}

export function isBuyersProgram(programType?: ProgramType) {
  return BuyerProgramsList.includes(programType as ProgramType);
}

export function isUnscheduledMeetingsPurchaseAllowed(programType?: ProgramType) {
  return programType && UnscheduledMeetingsPurchaseAllowedProgramsList.includes(programType);
}

export function isProgramCompleted(programType: ProgramType, status?: string) {
  const programCompletedStatusMap = {
    [ProgramType.BUYERS_PROGRAM]: BuyersProgramStatus.COMPLETED,
    [ProgramType.IN_PERSON_MEETUP_PROGRAM]: InPersonMeetupProgramStatus.COMPLETED,
    [ProgramType.MEETUP_PROGRAM]: MeetupProgramStatus.COMPLETED,
    [ProgramType.TABLETALK_PROGRAM]: TabletalkProgramStatus.COMPLETED,
    [ProgramType.ASSOCIATED_PROGRAM]: AssociatedProgramStatus.COMPLETED,
    [ProgramType.TRADE_PROGRAM]: TradeProgramStatus.COMPLETED,
    [ProgramType.CONNECTIONS_PROGRAM]: ConnectionsProgramStatus.COMPLETED
  };
  return Boolean(status && status === programCompletedStatusMap[programType]);
}

export function searchInObject<T>(obj: any, searchStr: string, keysToSearch: Array<keyof T> = []): boolean {
  if (!searchStr) {
    return true;
  }
  let isExist = false;
  if (!obj) {
    return isExist;
  }
  switch (typeof obj) {
    case "string":
    case "number":
    case "boolean":
      isExist = (obj as string).toString().toLowerCase().indexOf(searchStr.toString().toLowerCase()) !== -1;
      break;
    case "object":
      if (obj instanceof Array) {
        isExist = obj.reduce((previousValue, currentValue) => {
          return previousValue || searchInObject<T>(currentValue, searchStr, keysToSearch);
        }, isExist);
      } else {
        isExist = Boolean(
          Object.keys(obj)
            .filter(i => keysToSearch.includes(i as keyof T))
            .reduce((previousValue, currentValue) => {
              return previousValue || searchInObject<T>(obj[currentValue], searchStr, keysToSearch);
            }, isExist)
        );
      }
      break;
    default:
      isExist = false;
  }
  return isExist;
}

export function filterListByText<T>(list: Array<T>, searchString: string, keysToSearch: Array<keyof T> = []): Array<T> {
  return list.filter(item => searchInObject<T>(item, searchString.trim(), keysToSearch));
}

export function appendStringAsHtmlInBody(str: string) {
  if (!str?.trim?.()) return;
  const fragment = document.createRange().createContextualFragment(str);
  document.body.appendChild(fragment);
}

export function loadScriptFile({
  url,
  type = "text/javascript",
  async = true,
  defer = true
}: {
  url: string;
  type?: string;
  async?: boolean;
  defer?: boolean;
}) {
  const scriptTag = document.createElement("script");
  scriptTag.type = type;
  scriptTag.src = url;
  scriptTag.async = async;
  scriptTag.defer = defer;
  document.body.appendChild(scriptTag);
}

/**
 * Convert sentence in title case: example: not completed => Not Completed
 */
export function toTitleCase(str: string) {
  if (!str) {
    return "";
  }
  return str
    .split(" ")
    .map(word => capitalize(word.toLowerCase()))
    .join(" ");
}

export function isProgramInCompletedState(programType: ProgramType, state: string, status: StateStatus): boolean {
  if (![StateStatus.ON_GOING, StateStatus.ELAPSED].includes(status)) return false;
  switch (programType) {
    case ProgramType.IN_PERSON_MEETUP_PROGRAM:
      return state === InPersonMeetupProgramStatus.COMPLETED;
    case ProgramType.MEETUP_PROGRAM:
      return state === MeetupProgramStatus.COMPLETED;
    case ProgramType.BUYERS_PROGRAM:
      return state === BuyersProgramStatus.COMPLETED;
    case ProgramType.TABLETALK_PROGRAM:
      return state === TabletalkProgramStatus.COMPLETED;
    case ProgramType.TRADE_PROGRAM:
      return state === TradeProgramStatus.COMPLETED;
    case ProgramType.ASSOCIATED_PROGRAM:
      return state === AssociatedProgramStatus.COMPLETED;
    case ProgramType.CONNECTIONS_PROGRAM:
      return state === ConnectionsProgramStatus.COMPLETED;
  }
}

export function getDefaultProgramHeaderAsset(programType?: ProgramType, staticData?: StaticDataObject): string {
  if (!programType || !staticData?.programTypeDefaultHeaderImageMap) {
    return "";
  }
  return staticData.programTypeDefaultHeaderImageMap[programType] + `?ts=${new Date().getTime()}`;
}

export function isFileTypeImage(file: File) {
  return file.type.includes("image/");
}

export function getImageDimensionInfo(url: string): Promise<DimensionType> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height } as DimensionType);
    img.onerror = err => reject(err);
    img.src = url;
  });
}

export function getIntegerIfValidElseUndefined(value?: string) {
  if (value && !isNaN(Number(value))) {
    return parseInt(value);
  }
  return undefined;
}

export function getEntityLocation(label?: string, sector?: string) {
  return [label, sector].filter(i => Boolean(i)).join(", ");
}

export function getProgramStateValue(programType: ProgramType, staticData: StaticDataObject, state: string) {
  const programStatuses: AnyObject = {
    [ProgramType.IN_PERSON_MEETUP_PROGRAM]: staticData.inPersonMeetupProgramStatus[state],
    [ProgramType.MEETUP_PROGRAM]: staticData.meetupProgramStatus[state],
    [ProgramType.TABLETALK_PROGRAM]: staticData.tabletalkProgramStatus[state],
    [ProgramType.ASSOCIATED_PROGRAM]: staticData.associatedProgramStatus[state],
    [ProgramType.BUYERS_PROGRAM]: staticData.buyersProgramStatus[state],
    [ProgramType.TRADE_PROGRAM]: staticData.tradeProgramStatus[state],
    [ProgramType.CONNECTIONS_PROGRAM]: staticData.connectionsProgramStatus[state]
  };
  return programStatuses[programType] ?? "";
}

// returns how we want to display the payment method that was used for payment
//    e.g. for payment with card display last masked card no. with last four digits
export function getEncryptedPaymentToDisplay({
  staticData,
  transactionMethodType,
  transactionIdentifier,
  transactionIdentifierDisplayType
}: {
  staticData: StaticDataObject;
  transactionMethodType?: TransactionMethodType;
  transactionIdentifier?: string;
  transactionIdentifierDisplayType: TransactionIdentifierDisplayType;
}) {
  switch (transactionMethodType) {
    case TransactionMethodType.CREDIT_CARD:
      return `${staticData.transactionMethodType[transactionMethodType]} (${transactionIdentifier})`;
    case TransactionMethodType.GOOGLEPAY:
    case TransactionMethodType.APPLEPAY:
    case TransactionMethodType.ALIPAY:
    case TransactionMethodType.GRABPAY:
    case TransactionMethodType.PAYNOW:
    case TransactionMethodType.WECHATPAY:
    case TransactionMethodType.PAYPAL:
    case TransactionMethodType.LINK:
      return (
        <React.Fragment>
          {staticData.transactionMethodType[transactionMethodType]}{" "}
          {displayTransactionIdentifier(transactionIdentifierDisplayType, transactionIdentifier)}
        </React.Fragment>
      );
    case TransactionMethodType.OFFLINE:
      return `${staticData.transactionMethodType[transactionMethodType]}`;
    default:
      return null;
  }
}

export function getPaymentModeForPurchaseOrderInfo({
  copies,
  transactionMethodType,
  forwardedToInvoicePayer
}: {
  copies: Copies;
  transactionMethodType?: TransactionMethodType;
  forwardedToInvoicePayer?: boolean;
}) {
  if (forwardedToInvoicePayer) return copies.get("purchases.label.payment.type.invoice");

  switch (transactionMethodType) {
    case TransactionMethodType.CREDIT_CARD:
    case TransactionMethodType.GOOGLEPAY:
    case TransactionMethodType.APPLEPAY:
    case TransactionMethodType.ALIPAY:
    case TransactionMethodType.GRABPAY:
    case TransactionMethodType.PAYNOW:
    case TransactionMethodType.WECHATPAY:
    case TransactionMethodType.PAYPAL:
      return copies.get("purchases.label.payment.type.online");
    case TransactionMethodType.OFFLINE:
      return copies.get("purchases.label.payment.type.offline");
    default:
      return undefined;
  }
}

function displayTransactionIdentifier(
  transactionIdentifierDisplayType: TransactionIdentifierDisplayType,
  transactionIdentifier?: string
) {
  if (!transactionIdentifier) {
    return null;
  }

  switch (transactionIdentifierDisplayType) {
    case TransactionIdentifierDisplayType.SHOW:
      return `(${transactionIdentifier})`;
    case TransactionIdentifierDisplayType.TOOLTIP:
      return <ToolTip className="ml-2" content={transactionIdentifier} size={Size.SMALL} />;
    case TransactionIdentifierDisplayType.HIDE:
      return null;
  }
}

/**
 * @function isCurrentPathWhitelisted checks if an array of routes contains the provided path
 * @param routeArr array of full routes ['/faq', '/agenda-participation-invite/:id/:status']
 * @param exactMatch pass true for exact equality of path without params
 * @returns true if the actual route without path params is present in routeArr
 * @example checkIfPathWhitelisted(['/faq', '/agenda-participation-invite/:id/:status']) will check if `agenda-participation-invite` exists in the array provided (considering sample current path - '/agenda-participation-invite/123/a')
 */
export function isCurrentPathWhitelisted(routeArr: Array<string>, exactMatch?: boolean) {
  // splitting route by /, removing first /, picking the actual path at 0
  const path = window.location.pathname.split("/")?.slice(1)?.[0]?.toLowerCase() ?? "";
  const isRedirectRoute = path === MyExpRoute.REDIRECT.path.split("/")?.slice(1)?.[0]?.toLowerCase();
  if (isRedirectRoute) return isRedirectRouteWhitelisted(routeArr);
  return routeArr?.some(route => {
    const pathWithoutParams = route.split("/")?.slice(1)?.[0]?.toLowerCase() ?? "";
    return exactMatch ? pathWithoutParams === path : path !== "" && route.toLowerCase().includes(path);
  });
}

function isRedirectRouteWhitelisted(routeArr: Array<string>) {
  const params = new URLSearchParams(window.location.search);
  // allowing /redirect for stripe intermidiate payment pages
  const redirectRoute = params.get(RedirectQueryParam.ROUTE) ?? MyExpRoute.REDIRECT.path;
  return routeArr.includes(redirectRoute);
}

export function filterAgendaItemsBySearchTerm(arr: Array<ApiTimeSessionResponse>, searchTerm?: string) {
  const trimmedSearchTerm = searchTerm?.trim().toLowerCase();
  if (trimmedSearchTerm) {
    // search in all general attribute types, location, participant name, participant organization, agenda title
    return arr
      .map(o => {
        return {
          ...o,
          eventAgendaItems: o.eventAgendaItems.filter(i => {
            return (
              i.title.toLowerCase().includes(trimmedSearchTerm) ||
              i.location?.name.toLowerCase().includes(trimmedSearchTerm) ||
              i.metas.filter(m => m.title.toLowerCase().includes(trimmedSearchTerm)).length ||
              i.participations.filter(
                p =>
                  getFullName(p).toLowerCase().includes(trimmedSearchTerm) ||
                  p.organizationName.toLowerCase().includes(trimmedSearchTerm)
              ).length
            );
          }),
          timeSlots: []
        };
      })
      .filter(o => o.eventAgendaItems.length);
  }
  return arr;
}

export function getFileExtFromContentType(contentType?: MimeTypeEnum) {
  return contentType ? MIME_TYPE_FILE_EXT_MAP[contentType] || "" : "";
}

export function removeExtFromFileName(fileName: string) {
  const extInFile = Object.values(MIME_TYPE_FILE_EXT_MAP).find(ext => fileName.endsWith(ext));
  return extInFile ? replace(fileName, extInFile, "") : fileName;
}

/**
 * @param scriptToExec
 * @param dataObj - this will be available to script if they want to use them, dataObj will available to the
 * script as eval will be executing script at this function scope and in this scope dataObj will be available.
 */
export function executeStringAsScript(scriptToExec: string, dataObj: AnyObject = {}): boolean {
  try {
    // eslint-disable-next-line no-eval
    eval(`${scriptToExec}`);
    return true;
  } catch (e) {
    console.log("error while executing script", e);
    return false;
  }
}

export function getIconForAssetType(type?: string) {
  switch (type) {
    case MimeTypeEnum.CSV_MIME_TYPE:
      return <Icon.CSV />;
    case MimeTypeEnum.IMAGE_JPEG_MIME_TYPE:
    case MimeTypeEnum.IMAGE_JPG_MIME_TYPE:
    case MimeTypeEnum.IMAGE_PNG_MIME_TYPE:
    case MimeTypeEnum.IMAGE_SVG_MIME_TYPE:
      return <Icon.Image />;
    case MimeTypeEnum.PDF_MIME_TYPE:
    default:
      return <Icon.PdfFile />;
  }
}

export function isFormArrayFieldAnyRowInvalid(errors: AnyObject, fieldName: string) {
  return (
    !isEmpty(errors?.[fieldName]) &&
    Array.isArray(errors?.[fieldName]) &&
    errors?.[fieldName]?.some((err: AnyObject) => !isEmpty(err))
  );
}

export function convertSecondsToMilliseconds(seconds?: number) {
  if (isNaN(Number(seconds))) return undefined;
  return Math.round(Number(seconds) * 1000);
}

export function convertMillisecondsToSeconds(milliseconds?: number) {
  if (isNaN(Number(milliseconds))) return undefined;
  return Math.round(Number(milliseconds) / 1000);
}

// TODO: As of now used only in console can be refactored
export function isFeatureEnabled(args: {
  enabledFeatureMap?: ApiRegistrationEnabledFeatures;
  templateName?: PdfTemplateName;
}) {
  if (args.templateName) {
    return (
      args.enabledFeatureMap?.enabledRegistrationPdfTemplates.find(template => template.name === args.templateName)
        ?.enabled || false
    );
  }
  return false;
}

export function openInNewTab(urlToOpen?: string) {
  if (!urlToOpen) return;
  return window.open(urlToOpen, "_blank");
}

export async function hasClipboardPermissions() {
  try {
    await navigator.clipboard.writeText(""); // Test write
    await navigator.clipboard.readText(); // Test read
    return true;
  } catch (error) {
    return false;
  }
}

export function redirectToExternalURL(url: string) {
  window.location.replace(getUrlForHref(url));
}

export function shouldBypassMyExpWrapper(providerName: string) {
  return (BYPASS_MYEXP_WRAPPERS_FOR_ROUTES as AnyObject)[providerName].includes(window.location.pathname);
}

export function getBase64DecodedStringIfApplicable(str?: string | null, defaultValue = "") {
  if (!str) {
    return defaultValue;
  }

  const isEncoded = Base64Util.isBase64Encoded(str);
  return isEncoded ? Base64Util.decode(str) || defaultValue : str;
}
